import { FC, useMemo, useRef, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { useAtom } from 'jotai'
import * as Dialog from '@radix-ui/react-dialog'

import ListingCard from './ListingCard'
import SubmitHomeCard from './SubmitHomeCard'
import ListingContent from '../ListingContent/index'
import Pagination from '../Pagination'

import { Listing } from '../../services/getListings'
import { useListingsState } from '../../services/context/ListingsContext'
import { useTracking } from '../../services/context/TrackingContext'
import { useAppState } from '../../services/context/AppContext'
import { selectPayment } from '../../services/flags/selectPayment'

import { numberToCurrency } from 'utils'

import { LoadingSpinner, SecondaryButtonLink, TertiaryButton } from 'ui'

import { SearchIcon } from 'icons'

import { CalculatorPageContent } from '../../hooks/useAcreCalculator'

import { customerFavoriteListings } from '../../utils/atoms'

interface IListingsGridProps {
  listings?: Listing[]
  fetching?: boolean
  selectedHomeId?: number
  selectedHome?: Listing
  calculator: CalculatorPageContent
  regionalPage?: boolean
}

const getPaginatedListings = (
  featured: Record<number, Listing[]> = {},
  listings: Listing[] = [],
  totalCount: number,
  currentPage: number
) => {
  const listingsPerPage = 23

  let featuredUntilPageCount = 0

  for (const page of Array.from({ length: currentPage - 1 }, (_, i) => i + 1)) {
    featuredUntilPageCount += (featured[page] ?? []).length
  }

  const featuredOnPage = featured[currentPage] ?? []

  const startIndex =
    -featuredUntilPageCount + listingsPerPage * (currentPage - 1)
  const endIndex = startIndex + listingsPerPage - featuredOnPage.length

  let listingsOnPage = [
    ...featuredOnPage,
    ...listings.slice(startIndex, endIndex),
  ]

  const totalPages = Math.ceil(totalCount / listingsPerPage)

  if (currentPage === totalPages && listingsOnPage.length < listingsPerPage) {
    const featuredOnLaterPages: Listing[] = []

    const lastPageWithFeatures = Math.max(
      ...Object.keys(featured).map((page) => +page)
    )

    for (const page of Array.from(
      { length: lastPageWithFeatures - currentPage },
      (_, i) => i + currentPage + 1
    )) {
      featuredOnLaterPages.push(...(featured[page] ?? []))
    }

    listingsOnPage = [
      ...featuredOnPage,
      ...featuredOnLaterPages,
      ...listings.slice(startIndex, endIndex),
    ]
  }

  return { listingsOnPage, totalPages }
}

export const formatStandardListingProperties = (listing: Listing) => ({
  listing_id: listing.id,
  listing_bedroom_count: listing.beds,
  listing_bathroom_count: listing.baths,
  listing_sqft: listing.sqft,
  listing_address: listing.address,
  listing_price: listing.list_price,
  listing_estimated_sale_price: listing.estimatedSalePrice,
  listing_list_date: listing.list_date,
  listing_saver_monthly_payment: listing.paymentOptions.saver.acreMonthly,
  listing_boost_monthly_payment: listing.paymentOptions.boost.acreMonthly,
})

const ListingsGrid: FC<IListingsGridProps> = (props) => {
  const { t } = useTranslation('homes')
  const { track } = useTracking()
  const { pathname, query } = useRouter()
  const [clickedRef, setClickedRef] = useState<number | null>(null)
  const listingTriggerRefs = useRef<(HTMLAnchorElement | null)[]>([])
  const {
    state: listingsState,
    routePushPage,
    routerResetAllFilters,
    routePushIndex,
  } = useListingsState()
  const { p: currentPage, sortByMethod, address } = listingsState

  const [favoriteListings] = useAtom(customerFavoriteListings)
  const {
    state: { globalFlags },
  } = useAppState()

  const { featured, listings } = useMemo(() => {
    const sorted =
      props.listings?.sort((a, b) => {
        switch (sortByMethod) {
          case 'price_high_to_low':
            return a.paymentOptions.saver.amount > b.paymentOptions.saver.amount
              ? -1
              : a.paymentOptions.saver.amount < b.paymentOptions.saver.amount
              ? 1
              : 0

          case 'price_low_to_high':
            return a.paymentOptions.saver.amount > b.paymentOptions.saver.amount
              ? 1
              : a.paymentOptions.saver.amount < b.paymentOptions.saver.amount
              ? -1
              : 0
          case 'time_on_market_desc':
            return a.list_date > b.list_date
              ? 1
              : a.list_date < b.list_date
              ? -1
              : 0
          default:
            return a.list_date < b.list_date
              ? 1
              : a.list_date > b.list_date
              ? -1
              : 0
        }
      }) ?? []

    const { featured, listings } = sorted.reduce<{
      featured: Listing[]
      listings: Listing[]
    }>(
      (aggregator, listing) => {
        if (listing.featured_at_page !== undefined) {
          aggregator.featured.push(listing)
        } else {
          aggregator.listings.push(listing)
        }

        return aggregator
      },
      { featured: [], listings: [] }
    )

    return {
      featured: featured
        .sort(
          (lhs, rhs) =>
            (lhs.featured_at_page ?? 0) - (rhs.featured_at_page ?? 0)
        )
        .reduce<Record<number, Listing[]>>((featured, listing) => {
          const page = listing.featured_at_page ?? 0
          const feature = featured[page] ?? []

          feature.push(listing)

          featured[page] = feature

          return featured
        }, {}),
      listings,
    }
  }, [props.listings, sortByMethod])

  const { listingsOnPage, totalPages } = getPaginatedListings(
    featured,
    listings,
    props.listings?.length ?? 0,
    currentPage
  )

  const handleClose = () => {
    routePushIndex()
  }

  const selectedHomeIdExists =
    typeof props.selectedHomeId !== 'undefined' && props.selectedHomeId !== null

  return (
    <>
      <Dialog.Root open={selectedHomeIdExists} onOpenChange={handleClose}>
        {listingsOnPage && listingsOnPage.length ? (
          <section className="mx-auto max-w-1440 p-24 lg:px-48">
            <div aria-live="polite" className="sr-only">
              {address
                ? t('showingResultsForAddress', { address })
                : t('showingAllResults')}
            </div>
            <div className="mb-24 flex flex-wrap items-center justify-between gap-x-16 gap-y-8">
              <h2 className="hd-lg">
                {t('certified-title.heading')}
                <a
                  className="ml-8 whitespace-nowrap text-teal-3 underline body-sm"
                  href="https://discover.acrehomes.com/blog-posts/only-the-best-homes-are-acre-certified"
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('certified-title.learn-more')} →
                </a>
              </h2>
              <div className="flex w-full items-center gap-16 desktop-nav:w-auto">
                <span className="whitespace-nowrap text-grey-7 body-sm">
                  {t('certified-title.have-a-home')}
                </span>
                <Link
                  href={{
                    pathname,
                    query: query
                      ? { ...query, submitHome: true }
                      : { submitHome: true },
                  }}
                  shallow
                  passHref
                >
                  <SecondaryButtonLink className="block flex-1 whitespace-nowrap bg-white text-center desktop-nav:flex-none">
                    {t('submit-home.title')}
                  </SecondaryButtonLink>
                </Link>
              </div>
            </div>
            <ul className="grid auto-rows-max grid-cols-cards-auto-fill gap-24">
              {listingsOnPage.map((home: Listing, index: number) => {
                const info = `${home.beds} beds • ${home.baths} baths • ${home.sqft} Sq. Ft.`
                // Sorting and pagination above intentionally still key off the
                // v2 figure; only the displayed price is selected here.
                const payment = selectPayment(home, globalFlags)
                const monthlyPayment =
                  payment.kind === 'unavailable'
                    ? t('contactUsForPricing')
                    : payment.kind === 'v3'
                    ? numberToCurrency(payment.products.saver.base)
                    : numberToCurrency(home.paymentOptions.saver.acreMonthly)
                return (
                  <ListingCard
                    ref={(el) =>
                      listingTriggerRefs.current.splice(index, 1, el)
                    }
                    // Stable identity: an index key reuses subtrees across
                    // pagination/filter changes, letting per-instance state
                    // (e.g. MlsMark's failedSrc) leak between listings.
                    key={home.id}
                    id={home.id}
                    imgUrl={home.thumbnail_resolutions?.low}
                    info={info}
                    address={home.address}
                    monthlyPayment={monthlyPayment}
                    monthlyPaymentUnavailable={payment.kind === 'unavailable'}
                    listPrice={numberToCurrency(home.list_price)}
                    existingOffer={home.existing_offer}
                    reserved={home.property_status === 'reserved'}
                    boughtWithAcre={home.property_status === 'acre-bought'}
                    feedName={home.feedName}
                    isFavorite={favoriteListings
                      .map((l) => l.id)
                      .includes(home.id)}
                    onClick={() => {
                      setClickedRef(index)
                      track({
                        event: 'Property Listing Opened',
                        properties: formatStandardListingProperties(home),
                      })
                    }}
                    onSave={() => {
                      track({
                        event: 'Property Listing Saved',
                        properties: {
                          ...formatStandardListingProperties(home),
                          save_link_type: 'Icon',
                          save_action_description: 'Tap Heart Icon',
                          save_link_location: 'Listing Card',
                        },
                      })
                    }}
                  />
                )
              })}
              <li>
                <SubmitHomeCard className="h-full" />
              </li>
            </ul>
            <div className="mt-32 flex justify-center">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                setCurrentPage={(page) => {
                  routePushPage(page)
                  listingTriggerRefs.current[0]?.focus()
                }}
              />
            </div>
          </section>
        ) : !listingsOnPage.length && props.fetching ? (
          <LoadingSpinner colorClassName="text-teal-4" />
        ) : (
          <section className="mx-auto flex max-w-1440 flex-col items-center justify-center p-24 lg:px-48">
            <SearchIcon className="mb-24 stroke-green-3 rect-18 group-hover:stroke-white group-focus:stroke-white" />
            <p className="mb-20">
              {address ? t('noResultsForAddress') : t('noResults')}
            </p>
            {!props.regionalPage && (
              <TertiaryButton onClick={() => routerResetAllFilters()}>
                {t('clearSearch')}
              </TertiaryButton>
            )}
            <SubmitHomeCard className="max-w-324 mt-24 px-8 sm:max-w-320 sm:px-0" />
          </section>
        )}
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-modal grid place-items-center bg-black/70">
            <Dialog.Title asChild>
              <span className="sr-only">{t('listingModalLabel')}</span>
            </Dialog.Title>
            <Dialog.Content
              className="max-h-fill flex h-full max-h-[100vh] w-full max-w-[100vw] flex-col bg-white md:max-h-[95vh] md:max-w-[90vw] md:overflow-hidden md:rounded"
              onCloseAutoFocus={() =>
                clickedRef
                  ? listingTriggerRefs.current?.[clickedRef]?.focus()
                  : null
              }
            >
              {selectedHomeIdExists ? (
                <ListingContent
                  handleClose={handleClose}
                  listingId={props.selectedHomeId ?? 0}
                  listingInitialData={props.selectedHome}
                  calculator={props.calculator}
                />
              ) : null}
            </Dialog.Content>
          </Dialog.Overlay>
        </Dialog.Portal>
      </Dialog.Root>
    </>
  )
}

export default ListingsGrid
